import type { RtmStatus } from '@/lib/oi/rtmStatus';
import { type LngLat, SEARCH_SIZE_KM, clampSizeKm } from '@/lib/oi/searchArea';
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

/** One chip per `rtm_state` status (ENG-213). */
export type OiStatusFilter = RtmStatus;

const DEFAULT_FILTERS: Record<OiStatusFilter, boolean> = {
  Scheduled: true,
  Accepted: true,
  Active: true,
  'Non-Conforming': true,
  Contingent: true,
  Ended: false,
  Rejected: false,
  Unknown: true,
};

/** Pre-ENG-213 chip names → their rtm_state successors. */
const LEGACY_FILTER_KEYS: Record<string, OiStatusFilter> = {
  Approved: 'Accepted',
  Active: 'Active',
  Contingent: 'Contingent',
  'Non-Conforming': 'Non-Conforming',
  Completed: 'Ended',
};

function migrateFilters(saved: unknown): Record<OiStatusFilter, boolean> {
  const out = { ...DEFAULT_FILTERS };
  if (!saved || typeof saved !== 'object') return out;
  for (const [key, on] of Object.entries(saved as Record<string, unknown>)) {
    const next = LEGACY_FILTER_KEYS[key];
    if (next && typeof on === 'boolean') out[next] = on;
  }
  return out;
}

/** Mockup `#oi-filter-row` — which provenance buckets the OI list shows.
 *  `3rdparty` is the tickets' "External" (ENG-183). */
export type OiCategory = 'all' | 'internal' | 'partner' | '3rdparty';

/** Square DSS search area — `sizeKm` is the side length (spec D6). */
export type OiSearchArea = { center: LngLat | null; sizeKm: number };

interface OiStore {
  selectedId: string | null;
  /** Mockup `_focusOIPolygon` — set on row hover; oi map layer thickens stroke. */
  hoveredId: string | null;
  filters: Record<OiStatusFilter, boolean>;
  category: OiCategory;
  searchQuery: string;
  searchArea: OiSearchArea;
  /** TimelineScrubber hour offset; null = no hour filter (every stage shows).
   *  3rd Party starts unfiltered; picking a bar narrows to that hour. */
  selectedHour: number | null;
  select: (id: string | null) => void;
  hover: (id: string | null) => void;
  toggleFilter: (k: OiStatusFilter) => void;
  setCategory: (c: OiCategory) => void;
  setSearchQuery: (q: string) => void;
  setSearchArea: (patch: Partial<OiSearchArea>) => void;
  setSelectedHour: (h: number | null) => void;
}

export const useOiStore = create<OiStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set) => ({
          selectedId: null,
          hoveredId: null,
          filters: DEFAULT_FILTERS,
          // ENG-188 — only Internal is on by default.
          category: 'internal',
          searchQuery: '',
          searchArea: { center: null, sizeKm: SEARCH_SIZE_KM.default },
          selectedHour: null,
          select: (id) => set({ selectedId: id }),
          hover: (id) => set({ hoveredId: id }),
          toggleFilter: (k) => set((s) => ({ filters: { ...s.filters, [k]: !s.filters[k] } })),
          setCategory: (category) =>
            set((s) => ({
              category,
              selectedHour:
                category === '3rdparty' && s.category !== '3rdparty' ? null : s.selectedHour,
            })),
          setSearchQuery: (searchQuery) => set({ searchQuery }),
          setSearchArea: (patch) =>
            set((s) => ({
              searchArea: {
                center: patch.center !== undefined ? patch.center : s.searchArea.center,
                sizeKm:
                  patch.sizeKm !== undefined ? clampSizeKm(patch.sizeKm) : s.searchArea.sizeKm,
              },
            })),
          setSelectedHour: (selectedHour) => set({ selectedHour }),
        }),
        {
          name: 'flysafe-v2-oi',
          // v1 (ENG-213): status chips renamed to the rtm_state vocabulary.
          // v2 (ENG-313): new `Scheduled` chip — a saved set without it would
          // read as "off" and hide every Scheduled OI.
          version: 2,
          migrate: (persisted, version) => {
            let state = (persisted ?? {}) as { filters?: unknown };
            if (version < 1) state = { ...state, filters: migrateFilters(state.filters) };
            if (version < 2) {
              const saved = (state.filters ?? {}) as Record<string, boolean>;
              state = { ...state, filters: { Scheduled: true, ...saved } };
            }
            return state;
          },
          // Filter chips + box size survive a reload. Selection, search text
          // and the box centre are session state — a centre from another city
          // is worse than re-seeding from the map.
          partialize: (s) => ({
            filters: s.filters,
            searchArea: { center: null, sizeKm: s.searchArea.sizeKm },
          }),
        },
      ),
    ),
    { name: 'oi' },
  ),
);
